/**
 * Super Admin → System Monitoring
 * Live failure checks, resource utilisation, queues/workers, crons,
 * databases, Elasticsearch, Redis, network diagnostics, and alert configuration.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import swal from 'sweetalert';
import Sidebar_Progress from '../../../layouts/Admin_Sidebar_Progress.component';
import LoginHeaderOperational from '../../LoginHeaderOperational';
import { API_URL } from '../../../../constant/API_Settings';
import { fetchPlatformSuperAdminProfile } from '../../../../utils/platformAuth';
import {
  fetchSystemMonitor,
  failSystemMonitorReportPdf,
  processSystemMonitorReportPdfs,
  pruneSystemMonitorReports,
  restartSystemMonitorQueue,
  retrySystemMonitorReportPdf,
  saveSystemMonitorAlerts,
  startSystemMonitorQueue,
  stopSystemMonitorQueue,
  systemMonitorAuthHeaders,
  systemMonitorDiagnosticsUrl,
  testSystemMonitorAlert,
} from '../adminApi';
import '../admin-dashboard.css';
import './admin-system-monitor.css';

function alertFormFromConfig(config, catalog) {
  return {
    enabled: !!config?.enabled,
    email_enabled: config?.email_enabled !== false,
    sms_enabled: !!config?.sms_enabled,
    email_recipients: config?.email_recipients || '',
    sms_recipients: config?.sms_recipients || '',
    checks: config?.checks || Object.keys(catalog || {}),
    cooldown_minutes: config?.cooldown_minutes || 30,
    min_level: config?.min_level || 'warning',
    mail_account_id: config?.mail_account_id || '',
    sms_account_id: config?.sms_account_id || '',
    sms_template_id: config?.sms_template_id || '',
  };
}

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'online', label: 'Online users' },
  { id: 'resources', label: 'Resources' },
  { id: 'queues', label: 'Queues & workers' },
  { id: 'crons', label: 'Crons' },
  { id: 'databases', label: 'Databases' },
  { id: 'elastic', label: 'Elasticsearch' },
  { id: 'redis', label: 'Redis' },
  { id: 'network', label: 'Network' },
  { id: 'alerts', label: 'Alert configuration' },
];

function levelClass(level) {
  if (level === 'critical' || level === 'alert') return 'critical';
  if (level === 'warning' || level === 'busy') return 'warning';
  return 'ok';
}

function levelLabel(level) {
  if (level === 'critical' || level === 'alert') return 'Critical';
  if (level === 'warning' || level === 'busy') return 'Warning';
  if (level === 'ok') return 'OK';
  return level || '—';
}

function overallLabel(level) {
  if (level === 'critical') return 'Critical';
  if (level === 'warning') return 'Warning';
  return 'Healthy';
}

function Meter({ label, pct, hint }) {
  const value = pct == null ? 0 : Math.max(0, Math.min(100, Number(pct)));
  const tone = value >= 90 ? 'critical' : value >= 75 ? 'warning' : 'ok';
  return (
    <div className="sysmon-meter">
      <div className="sysmon-meter__head">
        <span>{label}</span>
        <strong>{pct == null ? 'n/a' : `${value}%`}</strong>
      </div>
      <div className="sysmon-meter__track">
        <div className={`sysmon-meter__fill sysmon-meter__fill--${tone}`} style={{ width: `${value}%` }} />
      </div>
      {hint ? <div className="sysmon-meter__hint">{hint}</div> : null}
    </div>
  );
}

function StatusDot({ level }) {
  return <span className={`sysmon-dot sysmon-dot--${levelClass(level)}`} />;
}

function CheckCard({ check }) {
  const recent = check?.detail?.recent || [];
  return (
    <div className={`sysmon-check sysmon-check--${levelClass(check?.level)}`}>
      <div className="sysmon-check__head">
        <span className="sysmon-check__name">
          <StatusDot level={check?.level} />
          {check?.label}
        </span>
        <span className={`sysmon-pill sysmon-pill--${levelClass(check?.level)}`}>
          {levelLabel(check?.level)}
        </span>
      </div>
      <p className="sysmon-check__summary">{check?.summary}</p>
      {recent.length > 0 && (
        <ul className="sysmon-recent">
          {recent.map((item, idx) => (
            <li key={`${item.name}-${idx}`}>
              <strong>{item.name}</strong>
              <span>{item.at}</span>
              {item.detail ? <em title={item.detail}>{item.detail}</em> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatStorageBytes(bytes) {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  if (value < 1048576) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1048576).toFixed(1)} MB`;
}

function Kv({ label, value }) {
  return (
    <div className="sysmon-kv">
      <span>{label}</span>
      <strong>{value == null || value === '' ? '—' : String(value)}</strong>
    </div>
  );
}

const AdminSystemMonitoring = (props) => {
  const history = useHistory();
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [queueBusy, setQueueBusy] = useState('');
  const [alertForm, setAlertForm] = useState(null);
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [netdiag, setNetdiag] = useState({ status: '', verdict: null, metrics: null });

  const load = useCallback((silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    return fetchSystemMonitor()
      .then((payload) => {
        setData(payload);
        if (!alertForm) {
          setAlertForm(alertFormFromConfig(payload.alert_config, payload.alert_catalog));
        }
      })
      .catch((err) => swal('Error', err.message || 'Could not load system monitoring', 'error'))
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  }, [alertForm]);

  useEffect(() => {
    fetchPlatformSuperAdminProfile(API_URL, localStorage.getItem('AUTH_TOKEN'))
      .then((profile) => (profile ? load(false) : history.goBack()))
      .catch(() => history.goBack());
  }, [history]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading) return undefined;
    const timer = setInterval(() => load(true), 30000);
    return () => clearInterval(timer);
  }, [loading, load]);

  const overall = data?.overall?.overall || 'ok';
  const checks = data?.overall?.checks || {};
  const failingCount = useMemo(
    () => Object.values(checks).filter((c) => c.level && c.level !== 'ok').length,
    [checks]
  );

  async function handleQueue(action, key, label) {
    const confirmText = `${action.charAt(0).toUpperCase() + action.slice(1)} the ${label} worker?`;
    const ok = await swal({ title: confirmText, icon: 'warning', buttons: true });
    if (!ok) return;
    setQueueBusy(`${action}:${key}`);
    const fn = action === 'start' ? startSystemMonitorQueue : action === 'stop' ? stopSystemMonitorQueue : restartSystemMonitorQueue;
    try {
      const res = await fn(key);
      swal(res.ok ? 'Done' : 'Failed', res.message, res.ok ? 'success' : 'error');
      await load(true);
    } catch (err) {
      swal('Error', err.message, 'error');
    } finally {
      setQueueBusy('');
    }
  }

  async function handleReportPrune(dryRun) {
    const storage = data?.report_storage || {};
    const days = storage.retention_days || 30;
    const stale = storage.stale_files || 0;
    const ok = await swal({
      title: dryRun ? 'Preview old report cleanup?' : 'Delete old reports?',
      text: dryRun
        ? `Count PDF, Excel and CSV files older than ${days} days. No files will be deleted.`
        : `This deletes only PDF, Excel and CSV report files older than ${days} days (${stale} file(s) ready). Other system files are not touched.`,
      icon: 'warning',
      buttons: true,
    });
    if (!ok) return;
    setQueueBusy(dryRun ? 'reports:preview' : 'reports:prune');
    try {
      const res = await pruneSystemMonitorReports({ days, dryRun });
      swal(res.ok ? (dryRun ? 'Preview' : 'Cleanup complete') : 'Could not finish', res.message, res.ok ? 'success' : 'error');
      await load(true);
    } catch (err) {
      swal('Error', err.message, 'error');
    } finally {
      setQueueBusy('');
    }
  }

  async function handlePdfProcess() {
    const ok = await swal({
      title: 'Process pending report PDFs?',
      text: 'Starts the Default worker, retries failed PDF jobs, and releases stuck reserved jobs.',
      icon: 'warning',
      buttons: true,
    });
    if (!ok) return;
    setQueueBusy('pdf:process');
    try {
      const res = await processSystemMonitorReportPdfs();
      swal(res.ok ? 'Done' : 'Could not finish', res.message, res.ok ? 'success' : 'error');
      await load(true);
    } catch (err) {
      swal('Error', err.message, 'error');
    } finally {
      setQueueBusy('');
    }
  }

  async function handlePdfRetry(row) {
    const ok = await swal({
      title: 'Regenerate this PDF?',
      text: 'Queues the report again with the same date range. The pump user will see Generating until the file is ready.',
      icon: 'info',
      buttons: true,
    });
    if (!ok) return;
    setQueueBusy(`pdf-retry:${row.id}`);
    try {
      const res = await retrySystemMonitorReportPdf(row.id);
      swal(res.ok ? 'Retry queued' : 'Cannot retry', res.message, res.ok ? 'success' : 'error');
      await load(true);
    } catch (err) {
      swal('Error', err.message, 'error');
    } finally {
      setQueueBusy('');
    }
  }

  async function handlePdfFail(row) {
    const ok = await swal({
      title: 'Mark this PDF as failed?',
      text: 'The pump user will see that this report could not be generated. The queue detail stays on this screen.',
      icon: 'warning',
      buttons: true,
    });
    if (!ok) return;
    setQueueBusy(`pdf-fail:${row.id}`);
    try {
      const res = await failSystemMonitorReportPdf(row.id, row.reason);
      swal(res.ok ? 'Marked failed' : 'Failed', res.message, res.ok ? 'success' : 'error');
      await load(true);
    } catch (err) {
      swal('Error', err.message, 'error');
    } finally {
      setQueueBusy('');
    }
  }

  function toggleCheck(key) {
    setAlertForm((prev) => {
      const has = prev.checks.includes(key);
      return {
        ...prev,
        checks: has ? prev.checks.filter((k) => k !== key) : [...prev.checks, key],
      };
    });
  }

  async function saveAlerts() {
    setSavingAlerts(true);
    try {
      const res = await saveSystemMonitorAlerts(alertForm);
      if (res.config) {
        setAlertForm(alertFormFromConfig(res.config, data?.alert_catalog));
      }
      swal('Saved', res.message, 'success');
      await load(true);
    } catch (err) {
      swal('Error', err.message, 'error');
    } finally {
      setSavingAlerts(false);
    }
  }

  async function sendTestAlert() {
    try {
      await saveSystemMonitorAlerts(alertForm);
      const res = await testSystemMonitorAlert({
        mail_account_id: alertForm.mail_account_id || null,
        sms_account_id: alertForm.sms_account_id || null,
        sms_template_id: alertForm.sms_template_id || null,
      });
      swal(res.status ? 'Sent' : 'Not sent', res.message, res.status ? 'success' : 'warning');
      await load(true);
    } catch (err) {
      swal('Error', err.message, 'error');
    }
  }

  async function runSpeedTest() {
    const headers = systemMonitorAuthHeaders();
    const PING_SAMPLES = 6;
    const PAYLOAD_BYTES = 262144;

    const bust = (url) => `${url}${url.includes('?') ? '&' : '?'}_=${Date.now()}${Math.random()}`;
    const timedFetch = async (url, options = {}) => {
      const start = performance.now();
      const response = await fetch(bust(url), { cache: 'no-store', headers, ...options });
      return { response, elapsedMs: performance.now() - start };
    };
    const fmt = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${Math.round(ms)} ms`);
    const levelOf = (value, warn, critical) => (value >= critical ? 'critical' : value >= warn ? 'warning' : 'ok');

    setNetdiag({ status: 'Running 6 HTTP round trips, a server probe and a throughput test…', verdict: null, metrics: null });
    try {
      const pings = [];
      for (let i = 0; i < PING_SAMPLES; i += 1) {
        const result = await timedFetch(systemMonitorDiagnosticsUrl('ping'));
        if (!result.response.ok) throw new Error(`Round-trip failed (HTTP ${result.response.status})`);
        await result.response.json();
        pings.push(result.elapsedMs);
      }

      const probeTimed = await timedFetch(systemMonitorDiagnosticsUrl('probe'));
      if (!probeTimed.response.ok) throw new Error(`Probe failed (HTTP ${probeTimed.response.status})`);
      const probeBody = await probeTimed.response.json();
      const probe = {
        totalMs: probeTimed.elapsedMs,
        serverMs: Number(probeBody.server_ms) || 0,
        dbOk: !!probeBody.db_ok,
      };

      const payloadTimed = await timedFetch(systemMonitorDiagnosticsUrl('payload', `size=${PAYLOAD_BYTES}`));
      if (!payloadTimed.response.ok) throw new Error(`Payload failed (HTTP ${payloadTimed.response.status})`);
      const buffer = await payloadTimed.response.arrayBuffer();
      const throughput = (buffer.byteLength / 1024) / (Math.max(payloadTimed.elapsedMs, 1) / 1000);

      const avg = pings.reduce((a, b) => a + b, 0) / pings.length;
      const min = Math.min(...pings);
      const max = Math.max(...pings);
      const jitter = max - min;
      const overhead = Math.max(0, probe.totalMs - probe.serverMs);

      const pingLevel = levelOf(avg, 80, 250);
      const serverLevel = levelOf(probe.serverMs, 50, 300);
      const throughputLevel = throughput < 100 ? 'critical' : throughput < 1000 ? 'warning' : 'ok';
      const networkBad = pingLevel !== 'ok' || jitter > 150;
      const serverBad = serverLevel !== 'ok';

      let bannerLevel = 'ok';
      let title = 'Network and server both look healthy.';
      let detail = `Round trip ${fmt(avg)} avg, server processing ${fmt(probe.serverMs)}.`;
      if (networkBad && serverBad) {
        bannerLevel = 'critical';
        title = 'Both your network and the server look slow right now.';
        detail = `The round trip is ${fmt(avg)} avg and the server itself took ${fmt(probe.serverMs)} to run one DB query.`;
      } else if (networkBad) {
        bannerLevel = pingLevel === 'critical' ? 'critical' : 'warning';
        title = 'The network between you and the server looks slow.';
        detail = `The round trip is ${fmt(avg)} avg, but the server answered a DB query in ${fmt(probe.serverMs)}.`;
      } else if (serverBad) {
        bannerLevel = serverLevel === 'critical' ? 'critical' : 'warning';
        title = 'The server looks slow, not your network.';
        detail = `The round trip is a healthy ${fmt(avg)} avg, but the server took ${fmt(probe.serverMs)} to run one small DB query.`;
      }
      if (throughputLevel !== 'ok') {
        detail += ` Download throughput is ${throughputLevel === 'critical' ? 'very low' : 'moderate'} (${Math.round(throughput)} KB/s).`;
        if (bannerLevel === 'ok') bannerLevel = throughputLevel === 'critical' ? 'warning' : 'ok';
      }

      setNetdiag({
        status: 'Done.',
        verdict: { bannerLevel, title, detail },
        metrics: {
          ping: fmt(avg),
          pingHint: `avg ${fmt(avg)} · min ${fmt(min)} · max ${fmt(max)} · jitter ${fmt(jitter)}`,
          server: `${fmt(probe.serverMs)}${probe.dbOk ? '' : ' (DB error)'}`,
          overhead: fmt(overhead),
          throughput: `${Math.round(throughput)} KB/s`,
        },
      });
    } catch (err) {
      setNetdiag({
        status: `Test failed: ${err.message || err}. Check your connection and try again.`,
        verdict: null,
        metrics: null,
      });
    }
  }

  const server = data?.server || {};
  const cron = data?.cron || {};
  const queues = data?.queues || [];
  const catalog = data?.alert_catalog || {};

  return (
    <div className="main-container page-wrap admin-platform sysmon">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={35} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="System Monitoring" />
          <div className="container pb-4 mt-3">
            <div className="sysmon-nav">
              <Link to="/admin_dashboard" className="small">← Control Center</Link>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => load(true)} disabled={refreshing}>
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>

            {loading ? (
              <div className="admin-dash-home__loading mt-3">
                <div className="admin-dash-skeleton admin-dash-skeleton--hero" />
                <div className="admin-dash-skeleton admin-dash-skeleton--card" />
              </div>
            ) : (
              <>
                <header className={`sysmon-banner sysmon-banner--${levelClass(overall)}`}>
                  <div>
                    <h2>Server monitoring</h2>
                    <p>
                      {server.hostname || 'host'} · {failingCount} check{failingCount === 1 ? '' : 's'} needing attention · updated {data?.overall?.generated_at || 'just now'}
                    </p>
                  </div>
                  <div className="sysmon-banner__status">
                    <span>{overallLabel(overall)}</span>
                    <small>Auto-refresh 30s</small>
                  </div>
                </header>

                <div className="sysmon-tabs">
                  {TABS.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`sysmon-tab ${tab === item.id ? 'is-active' : ''}`}
                      onClick={() => setTab(item.id)}
                    >
                      {item.id === 'online' && data?.online_users
                        ? `Online users (${data.online_users.count || 0})`
                        : item.label}
                    </button>
                  ))}
                </div>

                {tab === 'overview' && (
                  <section className="sysmon-grid">
                    {Object.values(checks).map((check) => (
                      <CheckCard key={check.key} check={check} />
                    ))}
                  </section>
                )}

                {tab === 'online' && (
                  <section className="admin-panel sysmon-panel">
                    <h5>Online users</h5>
                    <p className="sysmon-help">
                      People who have used the app in the last {data?.online_users?.window_minutes || 5} minutes.
                      The web app checks in every 30 seconds while it is open. Mobile users appear when the app calls the API.
                    </p>
                    {data?.online_users?.available === false ? (
                      <p className="sysmon-help mb-0">Online tracking is not ready yet. Run <code>php artisan migrate</code> on this server.</p>
                    ) : (
                      <>
                        <p className="mb-3">
                          <strong>{data?.online_users?.count || 0}</strong> user{(data?.online_users?.count || 0) === 1 ? '' : 's'} online
                          {' · '}
                          <strong>{data?.online_users?.pump_count || 0}</strong> pump{(data?.online_users?.pump_count || 0) === 1 ? '' : 's'}
                        </p>
                        <div className="table-responsive">
                          <table className="table table-sm sysmon-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Username</th>
                                <th>Pump</th>
                                <th>Role</th>
                                <th>Last activity</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(data?.online_users?.users || []).length === 0 ? (
                                <tr><td colSpan={5} className="text-muted">No one is using the app right now.</td></tr>
                              ) : data.online_users.users.map((user) => (
                                <tr key={user.id}>
                                  <td>{user.name}</td>
                                  <td>{user.user_name || '—'}</td>
                                  <td>{user.pump_name}</td>
                                  <td>{user.role_name}</td>
                                  <td>{user.last_seen_ago || user.last_seen_at || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </section>
                )}

                {tab === 'resources' && (
                  <section className="admin-panel sysmon-panel">
                    <h5>Resource utilisation</h5>
                    <p className="sysmon-help">CPU load, RAM and application volume on {server.hostname}. Thresholds: warning 75% / critical 90%.</p>
                    <div className="sysmon-meters">
                      <Meter
                        label="CPU"
                        pct={server.cpu_load_pct}
                        hint={`${server.cpu_cores || '?'} cores · 1m ${server.cpu_load_1m ?? '—'} · 5m ${server.cpu_load_5m ?? '—'}`}
                      />
                      <Meter
                        label="Memory"
                        pct={server.ram_used_pct}
                        hint={server.ram_total_gb != null ? `${server.ram_used_gb} / ${server.ram_total_gb} GB used` : `PHP ${server.php_memory_used_mb} MB / ${server.php_memory_limit}`}
                      />
                      <Meter
                        label="Disk"
                        pct={server.disk_used_pct}
                        hint={server.disk_total_gb != null ? `${server.disk_used_gb} / ${server.disk_total_gb} GB · ${server.disk_free_gb} GB free` : 'Unavailable'}
                      />
                    </div>
                    <div className="sysmon-kv-grid mt-3">
                      <Kv label="Hostname" value={server.hostname} />
                      <Kv label="OS" value={server.os} />
                      <Kv label="PHP" value={`${server.php_version} (${server.php_sapi})`} />
                      <Kv label="PHP memory" value={`${server.php_memory_used_mb} MB / ${server.php_memory_limit}`} />
                      <Kv label="Cache driver" value={data?.cache_driver} />
                      <Kv label="Queue driver" value={data?.queue_driver} />
                    </div>
                    {data?.redis?.configured && (
                      <div className={`sysmon-inline ${data.redis.ok ? 'is-ok' : 'is-bad'}`}>
                        <StatusDot level={data.redis.ok ? 'ok' : 'critical'} />
                        Redis: {data.redis.summary || (data.redis.ok ? 'OK' : 'Failed')}
                      </div>
                    )}

                    <h5 className="mt-4">Stored reports</h5>
                    <p className="sysmon-help">
                      Generated PDF, Excel and CSV files older than {data?.report_storage?.retention_days || 30} days
                      are removed automatically every night at 03:20, and again if that job is missed.
                      Use the buttons below to free disk now. Only report files inside the report folders are deleted.
                    </p>
                    <div className="sysmon-kv-grid mb-3">
                      <Kv label="Report files" value={data?.report_storage?.files ?? 0} />
                      <Kv label="Report size" value={formatStorageBytes(data?.report_storage?.bytes)} />
                      <Kv label="Older than retention" value={`${data?.report_storage?.stale_files ?? 0} · ${formatStorageBytes(data?.report_storage?.stale_bytes)}`} />
                      <Kv label="Oldest file" value={data?.report_storage?.oldest_at || '—'} />
                      <Kv label="Last cleanup" value={data?.report_storage?.last_pruned_at || 'Never'} />
                      <Kv label="Allowed types" value={(data?.report_storage?.allowed_extensions || ['pdf', 'xls', 'xlsx', 'csv']).join(', ')} />
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={!!queueBusy}
                        onClick={() => handleReportPrune(true)}
                      >
                        {queueBusy === 'reports:preview' ? 'Counting…' : 'Preview old reports'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={!!queueBusy}
                        onClick={() => handleReportPrune(false)}
                      >
                        {queueBusy === 'reports:prune' ? 'Deleting…' : `Delete reports older than ${data?.report_storage?.retention_days || 30} days`}
                      </button>
                    </div>
                  </section>
                )}

                {tab === 'queues' && (
                  <section className="admin-panel sysmon-panel">
                    <h5>Queues & workers</h5>
                    <p className="sysmon-help">
                      Pending / reserved / failed jobs and worker process status. Driver: <code>{data?.queue_driver}</code>.
                      Production should run <code>php artisan queue:work database --queue=default,email,sms</code>.
                    </p>
                    <div className="table-responsive">
                      <table className="table table-sm sysmon-table">
                        <thead>
                          <tr>
                            <th>Queue</th>
                            <th>Worker</th>
                            <th className="text-end">Pending</th>
                            <th className="text-end">Reserved</th>
                            <th className="text-end">Failed</th>
                            <th className="text-end">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {queues.length === 0 ? (
                            <tr><td colSpan={6} className="text-muted">No queue data</td></tr>
                          ) : queues.map((job) => (
                            <tr key={job.key}>
                              <td>
                                <StatusDot level={job.worker_status === 'stuck' || (job.worker_status === 'stopped' && job.pending) ? 'critical' : (job.worker_status === 'stopped' ? 'warning' : 'ok')} />
                                {job.label} <span className="text-muted">({job.queue})</span>
                              </td>
                              <td>{job.worker_status_label}</td>
                              <td className="text-end">{job.pending}</td>
                              <td className="text-end">{job.reserved}</td>
                              <td className="text-end">{job.failed}</td>
                              <td className="text-end">
                                {(job.can_start || job.can_stop || job.can_restart) ? (
                                  <div className="sysmon-actions">
                                    {job.can_start && (
                                      <button type="button" className="btn btn-sm btn-outline-success" disabled={!!queueBusy} onClick={() => handleQueue('start', job.key, job.label)}>Start</button>
                                    )}
                                    {job.can_stop && (
                                      <button type="button" className="btn btn-sm btn-outline-danger" disabled={!!queueBusy} onClick={() => handleQueue('stop', job.key, job.label)}>Stop</button>
                                    )}
                                    {job.can_restart && (
                                      <button type="button" className="btn btn-sm btn-outline-primary" disabled={!!queueBusy} onClick={() => handleQueue('restart', job.key, job.label)}>Restart</button>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted small" title={job.action_hint || ''}>{job.action_hint || '—'}</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {data?.queue_driver === 'sync' && (
                      <p className="sysmon-help mb-0">Queue driver is sync, so jobs run inline. Set QUEUE_CONNECTION=database in .env to manage background workers from this page.</p>
                    )}

                    <h5 className="mt-4">Pending report PDFs</h5>
                    <p className="sysmon-help">
                      Generating or failed export jobs from the last few hours (Trade Profit &amp; Loss, Daily Transaction, and other queued PDFs).
                      Exports left Generating for more than 3 hours with no live queue job are closed automatically.
                      Click <strong>Retry</strong> to regenerate a PDF with the same date range.
                      Click <strong>Process pending jobs now</strong> to reload workers, retry failed jobs, and re-queue lost jobs.
                      To free disk, delete stored PDF/Excel/CSV files from the <strong>Resources</strong> tab.
                    </p>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={!!queueBusy}
                        onClick={handlePdfProcess}
                      >
                        {queueBusy === 'pdf:process' ? 'Processing…' : 'Process pending jobs now'}
                      </button>
                      <span className="small text-muted align-self-center">
                        {(data?.report_pdfs?.generating || 0)} generating · {(data?.report_pdfs?.failed || 0)} failed
                        {data?.report_pdfs?.worker_running ? ' · Default worker running' : ' · Default worker stopped'}
                      </span>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-sm sysmon-table">
                        <thead>
                          <tr>
                            <th>Report</th>
                            <th>Pump</th>
                            <th>Age</th>
                            <th>Status</th>
                            <th>Reason</th>
                            <th className="text-end">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(data?.report_pdfs?.items || []).length === 0 ? (
                            <tr><td colSpan={6} className="text-muted">No generating or failed report PDFs.</td></tr>
                          ) : data.report_pdfs.items.map((row) => (
                            <tr key={row.id}>
                              <td>
                                <StatusDot level={row.queue_status === 'failed' ? 'critical' : (row.age_minutes > 5 ? 'warning' : 'ok')} />
                                {row.report_name}
                              </td>
                              <td>{row.pump_name}</td>
                              <td>{row.generated_at || '—'}{row.age_minutes ? ` (${row.age_minutes}m)` : ''}</td>
                              <td>{row.queue_status === 'queued' ? 'Generating' : 'Failed'}</td>
                              <td className="sysmon-reason">{row.reason || '—'}</td>
                              <td className="text-end">
                                <div className="sysmon-actions">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    disabled={!!queueBusy || row.can_retry === false}
                                    title={row.can_retry === false ? 'This report cannot be regenerated from the stored note.' : 'Regenerate this PDF'}
                                    onClick={() => handlePdfRetry(row)}
                                  >
                                    Retry
                                  </button>
                                  {row.can_fail && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      disabled={!!queueBusy}
                                      onClick={() => handlePdfFail(row)}
                                    >
                                      Mark failed
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {tab === 'crons' && (
                  <section className="admin-panel sysmon-panel">
                    <h5>Scheduler & crons</h5>
                    <div className={`sysmon-inline ${cron.scheduler_ok ? 'is-ok' : 'is-bad'}`}>
                      <StatusDot level={cron.scheduler_ok ? 'ok' : 'critical'} />
                      {cron.scheduler_ok
                        ? `Scheduler is active. Last activity ${cron.last_run_at || 'just now'} (${cron.last_job || 'heartbeat'}).`
                        : (cron.minutes_since_last == null
                          ? 'Scheduler has never run on this server. Add the crontab line below, then wait one minute.'
                          : `Scheduler is not running. Last activity was ${cron.minutes_since_last} minute(s) ago (${cron.last_run_at || 'unknown'}). That timestamp is an old log, not a live heartbeat. Add the crontab line below.`)}
                    </div>
                    {!cron.scheduler_ok && cron.install_command ? (
                      <p className="sysmon-help mt-3 mb-0">
                        On this server, as the user that owns the app:
                        <br />
                        <code>{cron.install_command}</code>
                      </p>
                    ) : null}
                    <div className="sysmon-kv-grid mt-3">
                      <Kv label="Runs (24h)" value={cron.runs_24h} />
                      <Kv label="Failures (24h)" value={cron.failed_24h} />
                      <Kv label="Heartbeat" value={cron.heartbeat_at} />
                    </div>
                    <h6 className="mt-4">Scheduled jobs</h6>
                    <div className="table-responsive">
                      <table className="table table-sm sysmon-table">
                        <thead><tr><th>Job</th><th>When</th><th>Cron</th></tr></thead>
                        <tbody>
                          {(cron.scheduled_jobs || []).map((job) => (
                            <tr key={job.key}>
                              <td>{job.label}</td>
                              <td>{job.when}</td>
                              <td><code>{job.expression}</code></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <h6 className="mt-3">Recent runs</h6>
                    {(cron.recent_runs || []).length === 0 ? (
                      <p className="text-muted mb-0">No rows in ps_cron_logs yet.</p>
                    ) : (
                      <ul className="sysmon-recent">
                        {(cron.recent_runs || []).map((row, idx) => (
                          <li key={`${row.api}-${idx}`}>
                            <StatusDot level={row.ok ? 'ok' : 'critical'} />
                            <strong>{row.api}</strong>
                            <span>{row.call_start_time}</span>
                            {row.error_msg ? <em>{row.error_msg}</em> : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                )}

                {tab === 'databases' && (
                  <section className="admin-panel sysmon-panel">
                    <h5>Database servers</h5>
                    <p className="sysmon-help">Primary connection plus optional extra MySQL hosts from SYSTEM_MONITOR_MYSQL_HOSTS.</p>
                    <div className={`sysmon-host ${data?.database?.ok ? 'is-ok' : 'is-bad'}`}>
                      <div className="sysmon-host__head">
                        <span><StatusDot level={data?.database?.ok ? 'ok' : 'critical'} /> {data?.database?.label || 'Primary'}</span>
                        <span className={`sysmon-pill sysmon-pill--${data?.database?.ok ? 'ok' : 'critical'}`}>{data?.database?.ok ? 'Up' : 'Down'}</span>
                      </div>
                      <div className="sysmon-kv-grid">
                        <Kv label="Driver" value={data?.database?.driver} />
                        <Kv label="Host" value={data?.database?.host ? `${data.database.host}:${data.database.port || ''}` : '—'} />
                        <Kv label="Database" value={data?.database?.database} />
                        <Kv label="Version" value={data?.database?.version} />
                        <Kv label="Connected" value={data?.database?.threads_connected} />
                        <Kv label="Active" value={data?.database?.threads_running} />
                        <Kv label="Uptime" value={data?.database?.uptime_human} />
                        {data?.database?.database_size_gb != null && <Kv label="Size" value={`${data.database.database_size_gb} GB`} />}
                        {data?.database?.slow_queries != null && <Kv label="Slow queries" value={data.database.slow_queries} />}
                      </div>
                    </div>
                    {(data?.mysql_servers || []).length === 0 ? (
                      <p className="sysmon-help mb-0 mt-3">No extra MySQL hosts configured.</p>
                    ) : (
                      <div className="sysmon-host-grid mt-3">
                        {data.mysql_servers.map((host) => (
                          <div key={host.label} className={`sysmon-host ${host.ok ? 'is-ok' : 'is-bad'}`}>
                            <div className="sysmon-host__head">
                              <span><StatusDot level={host.ok ? 'ok' : 'critical'} /> {host.label}</span>
                              <span className={`sysmon-pill sysmon-pill--${host.ok ? 'ok' : 'critical'}`}>{host.ok ? 'Up' : 'Down'}</span>
                            </div>
                            <div className="sysmon-kv-grid">
                              <Kv label="Version" value={host.version} />
                              <Kv label="Threads" value={host.threads_connected} />
                              {host.error && <Kv label="Error" value={host.error} />}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}

                {tab === 'elastic' && (
                  <section className="admin-panel sysmon-panel">
                    <h5>Elasticsearch</h5>
                    <p className="sysmon-help">Cluster health from SYSTEM_MONITOR_ELASTIC_HOSTS (GET /_cluster/health).</p>
                    {(data?.elasticsearch || []).length === 0 ? (
                      <p className="text-muted mb-0">Elasticsearch is not configured on this environment.</p>
                    ) : (
                      <div className="sysmon-host-grid">
                        {data.elasticsearch.map((host) => (
                          <div key={host.label} className={`sysmon-host ${host.ok ? 'is-ok' : 'is-bad'}`}>
                            <div className="sysmon-host__head">
                              <span><StatusDot level={host.ok ? 'ok' : 'critical'} /> {host.label}</span>
                              <span className={`sysmon-pill sysmon-pill--${host.status === 'green' ? 'ok' : (host.status === 'yellow' ? 'warning' : 'critical')}`}>
                                {host.status || (host.ok ? 'Up' : 'Down')}
                              </span>
                            </div>
                            <div className="sysmon-kv-grid">
                              <Kv label="Cluster" value={host.cluster_name} />
                              <Kv label="Nodes" value={host.number_of_nodes} />
                              <Kv label="Active shards" value={host.active_shards} />
                              <Kv label="Unassigned" value={host.unassigned_shards} />
                              {host.error && <Kv label="Error" value={host.error} />}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}

                {tab === 'redis' && (
                  <section className="admin-panel sysmon-panel">
                    <h5>Redis</h5>
                    <p className="sysmon-help">
                      TCP PING of REDIS_HOST and SYSTEM_MONITOR_REDIS_HOSTS. Independent of whether cache/queue currently uses Redis
                      (this environment: cache <code>{data?.cache_driver || data?.redis?.cache_driver}</code>, queue <code>{data?.queue_driver || data?.redis?.queue_driver}</code>).
                    </p>
                    {!data?.redis?.configured || (data?.redis?.servers || []).length === 0 ? (
                      <p className="text-muted mb-0">Redis is not configured on this environment. Set REDIS_HOST or SYSTEM_MONITOR_REDIS_HOSTS to enable this check.</p>
                    ) : (
                      <div className="sysmon-host-grid">
                        {data.redis.servers.map((host) => (
                          <div key={host.label} className={`sysmon-host ${host.ok ? 'is-ok' : 'is-bad'}`}>
                            <div className="sysmon-host__head">
                              <span><StatusDot level={host.ok ? 'ok' : 'critical'} /> {host.label}</span>
                              <span className={`sysmon-pill sysmon-pill--${host.ok ? 'ok' : 'critical'}`}>{host.ok ? 'Up' : 'Down'}</span>
                            </div>
                            <div className="sysmon-kv-grid">
                              <Kv label="Version" value={host.version} />
                              <Kv label="Role" value={host.role} />
                              <Kv label="Memory" value={host.used_memory} />
                              <Kv label="Peak memory" value={host.used_memory_peak} />
                              <Kv label="Clients" value={host.connected_clients} />
                              <Kv label="Latency" value={host.latency_ms != null ? `${host.latency_ms} ms` : null} />
                              {host.error && <Kv label="Error" value={host.error} />}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}

                {tab === 'network' && (
                  <section className="admin-panel sysmon-panel">
                    <h5>Network status</h5>
                    <p className="sysmon-help">Outbound HTTP reachability of configured targets (not ICMP ping).</p>
                    {(data?.network || []).length === 0 ? (
                      <p className="text-muted">No network targets configured.</p>
                    ) : (
                      <div className="table-responsive mb-4">
                        <table className="table table-sm sysmon-table">
                          <thead><tr><th>Target</th><th>Status</th><th>Latency</th></tr></thead>
                          <tbody>
                            {data.network.map((row) => (
                              <tr key={row.label}>
                                <td><StatusDot level={row.ok ? 'ok' : 'critical'} /> {row.label}</td>
                                <td>{row.ok ? (row.status || 'OK') : (row.error || 'Failed')}</td>
                                <td>{row.latency_ms != null ? `${row.latency_ms} ms` : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <h5>Connection speed test</h5>
                    <p className="sysmon-help">
                      Times a no-op HTTP round trip (not ICMP ping), a small database round trip measured on the server,
                      and a 256 KB download — then tells network slowness apart from server slowness.
                    </p>
                    <button type="button" className="btn btn-primary btn-sm" onClick={runSpeedTest}>Run test</button>
                    {netdiag.status && <span className="ms-2 small text-muted">{netdiag.status}</span>}
                    {netdiag.verdict && (
                      <div className={`sysmon-banner sysmon-banner--${netdiag.verdict.bannerLevel} mt-3`}>
                        <div>
                          <h2>{netdiag.verdict.title}</h2>
                          <p>{netdiag.verdict.detail}</p>
                        </div>
                        <div className="sysmon-banner__status">
                          <span>{overallLabel(netdiag.verdict.bannerLevel)}</span>
                        </div>
                      </div>
                    )}
                    {netdiag.metrics && (
                      <div className="sysmon-kv-grid mt-3">
                        <Kv label="Network round trip" value={netdiag.metrics.ping} />
                        <Kv label="Detail" value={netdiag.metrics.pingHint} />
                        <Kv label="Server processing" value={netdiag.metrics.server} />
                        <Kv label="Network overhead" value={netdiag.metrics.overhead} />
                        <Kv label="Download throughput" value={netdiag.metrics.throughput} />
                      </div>
                    )}
                  </section>
                )}

                {tab === 'alerts' && alertForm && (
                  <section className="admin-panel sysmon-panel">
                    <h5>Alert configuration</h5>
                    <p className="sysmon-help">
                      A scheduled command (<code>system:monitor-alerts</code>) repeats the live checks every five minutes.
                      If a watched check is warning or critical, Petrosmart emails / SMS the recipients below.
                      Repeat alerts for the same failure wait for the cooldown.
                    </p>
                    {data?.alert_config?.messaging_ready === false && (
                      <p className="sysmon-help">Email accounts and SMS templates are available after <code>php artisan migrate</code> runs on the server.</p>
                    )}

                    <label className="sysmon-toggle">
                      <input type="checkbox" checked={alertForm.enabled} onChange={(e) => setAlertForm({ ...alertForm, enabled: e.target.checked })} />
                      Enable failure alerts
                    </label>

                    <div className="row g-3 mt-1">
                      <div className="col-md-6">
                        <label className="sysmon-toggle">
                          <input type="checkbox" checked={alertForm.email_enabled} onChange={(e) => setAlertForm({ ...alertForm, email_enabled: e.target.checked })} />
                          Email
                        </label>
                        <textarea
                          className="form-control"
                          rows={3}
                          placeholder="ops@example.com, noc@example.com"
                          value={alertForm.email_recipients}
                          onChange={(e) => setAlertForm({ ...alertForm, email_recipients: e.target.value })}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="sysmon-toggle">
                          <input type="checkbox" checked={alertForm.sms_enabled} onChange={(e) => setAlertForm({ ...alertForm, sms_enabled: e.target.checked })} />
                          SMS
                        </label>
                        <textarea
                          className="form-control"
                          rows={3}
                          placeholder="9876543210, 9123456780"
                          value={alertForm.sms_recipients}
                          onChange={(e) => setAlertForm({ ...alertForm, sms_recipients: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="row g-3 mt-2">
                      <div className="col-md-4">
                        <label className="form-label">Minimum level</label>
                        <select className="form-select form-control" value={alertForm.min_level} onChange={(e) => setAlertForm({ ...alertForm, min_level: e.target.value })}>
                          <option value="warning">Warning and critical</option>
                          <option value="critical">Critical only</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Cooldown (minutes)</label>
                        <input
                          type="number"
                          min={5}
                          max={1440}
                          className="form-control"
                          value={alertForm.cooldown_minutes}
                          onChange={(e) => setAlertForm({ ...alertForm, cooldown_minutes: Number(e.target.value) })}
                        />
                      </div>
                    </div>

                    <h6 className="mt-4">Send with</h6>
                    <p className="sysmon-help">
                      Create email accounts, SMS accounts, and approved templates in <Link to="/admin-master-settings">Master Settings → Messaging</Link>.
                      Then select them here and click Save. Send test uses these choices, not the server MAIL_HOST.
                    </p>
                    {(data?.message_options?.mail_accounts || []).length === 0 && (
                      <p className="sysmon-help">No email account is saved yet. The live server MAIL_HOST is a local mailbox (127.0.0.1:1025), so email will not leave the server until you add an SMTP account.</p>
                    )}
                    {alertForm.sms_enabled && (data?.message_options?.sms_templates || []).length === 0 && (
                      <p className="sysmon-help">No approved SMS template is saved yet. Add an SMS account and template under Master Settings → Messaging before testing SMS.</p>
                    )}
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label">Email account</label>
                        <select
                          className="form-select form-control"
                          value={alertForm.mail_account_id || ''}
                          onChange={(e) => setAlertForm({ ...alertForm, mail_account_id: e.target.value })}
                        >
                          <option value="">Select an email account</option>
                          {(data?.message_options?.mail_accounts || []).map((row) => (
                            <option key={row.id} value={row.id}>
                              {row.name}{row.is_active ? '' : ' (inactive)'} — {row.from_address}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">SMS account</label>
                        <select
                          className="form-select form-control"
                          value={alertForm.sms_account_id || ''}
                          onChange={(e) => {
                            const smsAccountId = e.target.value;
                            const templates = data?.message_options?.sms_templates || [];
                            const stillValid = templates.some((row) => String(row.id) === String(alertForm.sms_template_id) && String(row.sms_account_id) === String(smsAccountId));
                            setAlertForm({
                              ...alertForm,
                              sms_account_id: smsAccountId,
                              sms_template_id: stillValid ? alertForm.sms_template_id : '',
                            });
                          }}
                        >
                          <option value="">Select an account</option>
                          {(data?.message_options?.sms_accounts || []).map((row) => (
                            <option key={row.id} value={row.id}>
                              {row.name}{row.is_active ? '' : ' (inactive)'}{row.sender_id ? ` — ${row.sender_id}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Approved SMS template</label>
                        <select
                          className="form-select form-control"
                          value={alertForm.sms_template_id || ''}
                          onChange={(e) => {
                            const templateId = e.target.value;
                            const template = (data?.message_options?.sms_templates || []).find((row) => String(row.id) === String(templateId));
                            setAlertForm({
                              ...alertForm,
                              sms_template_id: templateId,
                              sms_account_id: template ? String(template.sms_account_id) : alertForm.sms_account_id,
                            });
                          }}
                        >
                          <option value="">Select a template</option>
                          {(data?.message_options?.sms_templates || [])
                            .filter((row) => !alertForm.sms_account_id || String(row.sms_account_id) === String(alertForm.sms_account_id))
                            .map((row) => (
                              <option key={row.id} value={row.id}>
                                {row.name}{row.is_active ? '' : ' (inactive)'} — {row.dlt_template_id}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                    {(() => {
                      const selected = (data?.message_options?.sms_templates || []).find((row) => String(row.id) === String(alertForm.sms_template_id));
                      return selected ? <p className="sysmon-help">{selected.body}</p> : null;
                    })()}

                    <h6 className="mt-4">Watched checks</h6>
                    <div className="sysmon-check-picks">
                      {Object.entries(catalog).map(([key, meta]) => (
                        <label key={key} className="sysmon-pick">
                          <input
                            type="checkbox"
                            checked={alertForm.checks.includes(key)}
                            onChange={() => toggleCheck(key)}
                          />
                          <span>
                            <strong>{meta.label || key}</strong>
                            <small>{meta.description}</small>
                          </span>
                        </label>
                      ))}
                    </div>

                    <div className="sysmon-actions mt-3">
                      <button type="button" className="btn btn-primary" onClick={saveAlerts} disabled={savingAlerts}>
                        {savingAlerts ? 'Saving…' : 'Save alert settings'}
                      </button>
                      <button type="button" className="btn btn-outline-secondary" onClick={sendTestAlert}>
                        Send test alert
                      </button>
                    </div>

                    {data?.last_alert && (
                      <p className="sysmon-help mt-3 mb-0">
                        Last alert: {data.last_alert.at}
                        {data.last_alert.test ? ' (test)' : ''}
                        {data.last_alert.channels?.length ? ` via ${data.last_alert.channels.join(', ')}` : ''}
                      </p>
                    )}

                    <h6 className="mt-4">Send log</h6>
                    <p className="sysmon-help">Each email and SMS attempt, including the text that was sent and the provider response.</p>
                    {(data?.alert_deliveries || []).length === 0 ? (
                      <p className="text-muted">No alert has been sent yet.</p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm sysmon-table">
                          <thead>
                            <tr>
                              <th>Time</th>
                              <th>Channel</th>
                              <th>Recipient</th>
                              <th>Result</th>
                              <th>Message</th>
                              <th>Provider response</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.alert_deliveries.map((row) => (
                              <tr key={row.id}>
                                <td>{row.created_at}{row.is_test ? ' test' : ''}</td>
                                <td>{row.channel}{row.http_status ? ` ${row.http_status}` : ''}</td>
                                <td>{row.recipient}</td>
                                <td><StatusDot level={row.ok ? 'ok' : 'critical'} /> {row.ok ? 'Accepted' : (row.error || 'Failed')}</td>
                                <td className="sysmon-delivery-response">{row.message}</td>
                                <td className="sysmon-delivery-response">{row.provider_response || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSystemMonitoring;
